// src/components/admin/DailyTokenCounter.jsx
import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { getDailyTokenUsage } from '../../api/backendAiService';

const DAILY_LIMIT = 200000;

const MODULE_LABELS = {
  core:           'Core AI',
  bookings:       'Bookings AI',
  communications: 'Communications AI',
  loyalty:        'Loyalty AI',
  reports:        'Reports AI',
  customerPortal: 'Customer Portal AI',
};

function fmt(n) {
  return (n || 0).toLocaleString();
}

const DailyTokenCounter = forwardRef(function DailyTokenCounter({ salonId, onLimitReached }, ref) {
  const [usage, setUsage] = useState({ byModule: {}, totalTokens: 0, limitExceeded: false });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!salonId) return;
    try {
      const data = await getDailyTokenUsage(salonId);
      setUsage(data);
      if (onLimitReached) onLimitReached(data.limitExceeded || data.totalTokens >= DAILY_LIMIT);
    } catch {
      // silent — non-critical UI element
    } finally {
      setLoading(false);
    }
  }, [salonId, onLimitReached]);

  const update = useCallback((data) => {
    if (!data) return;
    setUsage(data);
    setLoading(false);
    if (onLimitReached) onLimitReached(data.limitExceeded || (data.totalTokens || 0) >= DAILY_LIMIT);
  }, [onLimitReached]);

  useImperativeHandle(ref, () => ({ refresh, update }), [refresh, update]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) return null;

  const total = usage.totalTokens || 0;
  const exceeded = usage.limitExceeded || total >= DAILY_LIMIT;

  const containerStyle = {
    margin: '0 0 10px 0',
    padding: '6px 12px',
    background: exceeded ? '#fff5f5' : '#f8f8f8',
    border: `1px solid ${exceeded ? '#fca5a5' : '#e0e0e0'}`,
    borderRadius: 6,
    fontSize: 11,
    color: exceeded ? '#b91c1c' : '#666',
    lineHeight: 1.6,
  };

  const rowStyle = {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    alignItems: 'center',
  };

  const moduleEntries = Object.keys(MODULE_LABELS).map(key => ({
    key,
    label: MODULE_LABELS[key],
    tokens: usage.byModule?.[key]?.totalTokens || 0,
  }));

  return (
    <div style={containerStyle}>
      {exceeded && (
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12 }}>
          Daily AI usage limit reached. AI is unavailable until 00:00:01.
        </div>
      )}
      <div style={rowStyle}>
        <span style={{ fontWeight: 500, color: exceeded ? '#b91c1c' : '#444' }}>
          AI Usage Today:
        </span>
        {moduleEntries.map(({ key, label, tokens }) => (
          <span key={key}>
            {label}: <strong>{fmt(tokens)}</strong>
          </span>
        ))}
        <span style={{ borderLeft: '1px solid #ccc', paddingLeft: 12 }}>
          Total: <strong style={{ color: exceeded ? '#b91c1c' : total > 160000 ? '#d97706' : '#333' }}>
            {fmt(total)} / {fmt(DAILY_LIMIT)}
          </strong>
        </span>
      </div>
      <div style={{ fontSize: 9, color: '#aaa', marginTop: 3 }}>
        Maximum 200,000 tokens per day across all AI modules. Limit resets at 00:00:01.
      </div>
    </div>
  );
});

export default DailyTokenCounter;
